import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8000/api";

export default function ProductsMenu({ onLinkClick }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hoveredCat, setHoveredCat] = useState(null); // category_id
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // 1. Fetch flat categories list once when the full page mounts
  useEffect(() => {
    const fetchMenuData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/categorilist`).then((r) => r.json());
        setCategories(res.data ?? res ?? []); 
      } catch (error) {
        console.error("Failed to fetch menu categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, []);

  const handleMouseLeaveMenu = () => {
    setOpen(false);
    setHoveredCat(null);
  };

  // Filter out active root categories (where parent_id is null or empty)
  const parentCats = categories.filter(
    (c) => !c.parent_id && c.status === "active"
  );

  // Dynamic grouping logic matching your flat array structure 
  const getSubcategories = (parentId) => {
    if (!parentId) return [];
    return categories.filter(
      (c) => c.parent_id === parentId && c.status === "active"
    );
  };

  // Pure client-side route tracking (No runtime API overhead)
  const handleCategoryClick = (catId) => {
    navigate(`/products?category=${catId}`);
    setOpen(false);
    setHoveredCat(null);
    onLinkClick?.();
  };

  return (
    <li
      ref={menuRef}
      className="relative group"
      onMouseEnter={() => setOpen(true)} // Open dropdown on hover
      onMouseLeave={handleMouseLeaveMenu} // Close dropdown when mouse leaves
    >
      {/* Menu Trigger Button */}
      <button
        onClick={() => {
          navigate("/products");
          setOpen(false);
          onLinkClick?.();
        }}
        className={`flex items-center gap-1 text-sm font-medium px-4 py-3 transition-colors ${
          open ? "text-primary" : "text-dark hover:text-primary"
        }`}
      >
        Products{" "}
        <FaChevronDown
          size={9}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Main Categories Dropdown Box */}
      {open && (
        <div
          className="absolute top-full left-0 bg-white border border-border-color shadow-xl z-30 flex flex-col py-2"
          style={{ minWidth: 230 }}
        >
          {loading && (
            <div className="p-4 text-xs text-gray-400 w-full text-center">
              Loading...
            </div>
          )}

          {!loading && (
            <>
              {parentCats.length === 0 && (
                <p className="px-4 py-3 text-xs text-gray-400">
                  No categories
                </p>
              )}

              {parentCats.map((cat) => {
                const subs = getSubcategories(cat.category_id);
                const hasSubs = subs.length > 0;
                const isActive = hoveredCat === cat.category_id;

                return (
                  <div key={cat.category_id} className="w-full">
                    {/* Parent Row */}
                    <div
                      className={`flex items-center justify-between px-4 py-2.5 cursor-pointer text-xs font-medium transition-colors ${
                        isActive
                          ? "bg-gray-50 text-primary border-r-2 border-primary"
                          : "text-dark hover:bg-gray-50 hover:text-primary"
                      }`}
                      onMouseEnter={() => setHoveredCat(cat.category_id)}
                      onClick={() => handleCategoryClick(cat.category_id)}
                    >
                      <span className="capitalize">{cat.name}</span>
                      {hasSubs && (
                        <FaChevronRight 
                          size={8} 
                          className={`text-gray-400 transition-transform duration-150 ${isActive ? "rotate-90" : ""}`} 
                        />
                      )}
                    </div>

                    {/* Inline Subcategory Box — opens right underneath its own active parent item */}
                    {isActive && hasSubs && (
                      <div className="bg-gray-50 border-y border-gray-100 py-1 pl-2">
                        {subs.map((sub) => (
                          <div
                            key={sub.category_id}
                            onClick={(e) => {
                              e.stopPropagation(); // Avoid triggering parent onClick
                              handleCategoryClick(sub.category_id);
                            }}
                            className="flex items-center gap-2 pl-5 pr-4 py-1.5 text-[11px] text-gray-500 hover:text-primary cursor-pointer transition-colors capitalize"
                          >
                            <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                            {sub.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* View All Catch Link */}
              <div className="border-t border-border-color mt-2 pt-2">
                <Link
                  to="/products"
                  onClick={() => {
                    setOpen(false);
                    onLinkClick?.();
                  }}
                  className="block px-4 py-2 text-xs font-medium text-primary hover:bg-gray-50 transition-colors"
                >
                  View All Products →
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}